import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Checkbox from '../../../components/ui/Checkbox';

const AlertSettings = ({ isOpen, onClose, currentSettings, onSave }) => {
  const [settings, setSettings] = useState(currentSettings);

  const deliveryMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'push', label: 'Push Notification' },
    { value: 'webhook', label: 'Webhook' }
  ];

  const confidenceThresholdOptions = [
    { value: '50', label: '50% and above' },
    { value: '60', label: '60% and above' },
    { value: '70', label: '70% and above' },
    { value: '80', label: '80% and above' },
    { value: '90', label: '90% and above' }
  ];

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-card-foreground">Alert Settings</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
          />
        </div>

        <div className="p-6 space-y-6">
          {/* Notification Preferences */}
          <div>
            <h3 className="text-lg font-medium text-card-foreground mb-4">Notification Preferences</h3>
            <div className="space-y-4">
              <Checkbox
                label="Enable signal alerts"
                description="Receive notifications when new signals are generated"
                checked={settings?.enableAlerts}
                onChange={(e) => handleSettingChange('enableAlerts', e?.target?.checked)}
              />

              <Checkbox
                label="High confidence signals only"
                description="Only receive alerts for signals with 80%+ confidence"
                checked={settings?.highConfidenceOnly}
                onChange={(e) => handleSettingChange('highConfidenceOnly', e?.target?.checked)}
              />

              <Checkbox
                label="Urgent signals"
                description="Immediate notifications for time-sensitive signals"
                checked={settings?.urgentSignals}
                onChange={(e) => handleSettingChange('urgentSignals', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Delivery Methods */}
          <div>
            <h3 className="text-lg font-medium text-card-foreground mb-4">Delivery Methods</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Primary Method"
                options={deliveryMethodOptions}
                value={settings?.primaryMethod}
                onChange={(value) => handleSettingChange('primaryMethod', value)}
              />

              <Select
                label="Secondary Method"
                options={[{ value: 'none', label: 'None' }, ...deliveryMethodOptions]}
                value={settings?.secondaryMethod}
                onChange={(value) => handleSettingChange('secondaryMethod', value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label="Email Address"
                type="email"
                placeholder="your@email.com"
                value={settings?.email}
                onChange={(e) => handleSettingChange('email', e?.target?.value)}
                disabled={!settings?.primaryMethod?.includes('email') && !settings?.secondaryMethod?.includes('email')}
              />

              <Input
                label="Phone Number"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={settings?.phone}
                onChange={(e) => handleSettingChange('phone', e?.target?.value)}
                disabled={!settings?.primaryMethod?.includes('sms') && !settings?.secondaryMethod?.includes('sms')}
              />
            </div>
          </div>

          {/* Signal Criteria */}
          <div>
            <h3 className="text-lg font-medium text-card-foreground mb-4">Signal Criteria</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Minimum Confidence"
                options={confidenceThresholdOptions}
                value={settings?.minConfidence}
                onChange={(value) => handleSettingChange('minConfidence', value)}
              />

              <Input
                label="Watchlist Only"
                type="text"
                placeholder="BTC, ETH, ADA (comma separated)"
                value={settings?.watchlist}
                onChange={(e) => handleSettingChange('watchlist', e?.target?.value)}
                description="Leave empty for all cryptocurrencies"
              />
            </div>
          </div>

          {/* Timing Settings */}
          <div>
            <h3 className="text-lg font-medium text-card-foreground mb-4">Timing Settings</h3>
            <div className="space-y-4">
              <Checkbox
                label="Quiet hours"
                description="Disable notifications during specified hours"
                checked={settings?.quietHours}
                onChange={(e) => handleSettingChange('quietHours', e?.target?.checked)}
              />

              {settings?.quietHours && (
                <div className="grid grid-cols-2 gap-4 ml-6">
                  <Input
                    label="Start Time"
                    type="time"
                    value={settings?.quietStart}
                    onChange={(e) => handleSettingChange('quietStart', e?.target?.value)}
                  />
                  <Input
                    label="End Time"
                    type="time"
                    value={settings?.quietEnd}
                    onChange={(e) => handleSettingChange('quietEnd', e?.target?.value)}
                  />
                </div>
              )}

              <Checkbox
                label="Weekend alerts"
                description="Receive alerts during weekends"
                checked={settings?.weekendAlerts}
                onChange={(e) => handleSettingChange('weekendAlerts', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            iconName="Save"
            iconPosition="left"
          >
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlertSettings;